var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var LoggedInUser_1;
/** @file This file defines the users used in the vue app. */
import moment from 'moment';
import { Left, Right } from 'purify-ts';
import * as apiModels from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { NonEmptyList } from '@codegrade/non-empty-list';
import { array, expr, immutable } from '@codegrade/utils';
import { GlobalPermissions as GPerm } from '@/permissions';
import { UserStore } from '@/store';
/** The user that is currently logged in. */
let LoggedInUser = LoggedInUser_1 = class LoggedInUser extends apiModels.NormalUser {
    /** The session of the user. */
    #sessions;
    /** All sessions of the user. */
    get sessions() {
        return this.#sessions;
    }
    /** The method this user used to log in. */
    loginMethod;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    permissions;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canSeeHidden;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    email;
    /** If the email is verified. Can be a Nothing if the email is also a Nothing. */
    emailVerified;
    /**
     * Is the user fully loaded.
     *
     * When we load the user from localStorage we don't have all properties correctly set just yet,
     * this boolean indicates if we retrieved those properties already.
     */
    fullyLoaded;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data, extraData) {
        super(data);
        this.#sessions = NonEmptyList(extraData.sessions);
        this.loginMethod = extraData.loginMethod;
        this.permissions = extraData.permissions;
        this.canSeeHidden = extraData.canSeeHidden;
        this.email = extraData.email;
        this.fullyLoaded = extraData.fullyLoaded;
        this.emailVerified = extraData.emailVerified;
    }
    /** @returns The current active session of the user. */
    get session() {
        return array.last(this.sessions);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    setPermission(perm, value) {
        const newPerms = Object.freeze({ ...this.permissions, [perm]: value });
        return new LoggedInUser_1(this, {
            sessions: this.sessions,
            loginMethod: this.loginMethod,
            permissions: newPerms,
            canSeeHidden: this.canSeeHidden,
            email: this.email,
            emailVerified: this.emailVerified,
            fullyLoaded: this.fullyLoaded,
        });
    }
    /**
     * This instance.
     *
     * @deprecated This has no point and is only here for backwards compatibility.
     */
    get user() {
        return this;
    }
    /**
     * Removes the most recent session from the user's session list and returns the updated user
     * instance.
     *
     * If removing the session would leave the user without any sessions, an error is returned.
     *
     * @returns Either an error if trying to remove the last session or an object containing the old
     * session and a new user instance.
     */
    popSession() {
        const newSessions = this.sessions.slice();
        const oldSession = newSessions.pop();
        if (!NonEmptyList.isNonEmpty(newSessions) || oldSession == null) {
            return Left(new Error('Cannot remove last active session'));
        }
        const newUser = new LoggedInUser_1(this, {
            canSeeHidden: this.canSeeHidden,
            email: this.email,
            sessions: newSessions,
            loginMethod: this.loginMethod,
            permissions: this.permissions,
            fullyLoaded: this.fullyLoaded,
            emailVerified: this.emailVerified,
        });
        return Right({ oldSession, newUser });
    }
    /**
     * Adds a new session to the user's current list of sessions, creating a new user instance with
     * the updated session list.
     *
     * The session list is managed as a non-empty list, ensuring the user always has at least one
     * active session.
     *
     * @param session The session to be added to the user's current sessions.
     * @returns A new instance of LoggedInUser with the updated session list.
     */
    addSession(session) {
        return new LoggedInUser_1(this, {
            canSeeHidden: this.canSeeHidden,
            email: this.email,
            emailVerified: this.emailVerified,
            sessions: NonEmptyList(this.sessions).concat([session]),
            loginMethod: this.loginMethod,
            permissions: this.permissions,
            fullyLoaded: this.fullyLoaded,
        });
    }
    /**
     *
     * Replaces the topmost session with a new session, creating a new user instance with
     * the updated session list.
     *
     * This method maintains the same session stack depth while updating the current active session.
     * This is useful for cases like assignment password verification where you want to modify
     * the current session restrictions without creating additional session layers.
     *
     * @param newSession The session to replace the current topmost session with.
     * @returns A new instance of LoggedInUser with the updated session list.
     */
    replaceCurrentSession(newSession) {
        const newSessionsArray = this.sessions.concat();
        newSessionsArray[newSessionsArray.length - 1] = newSession;
        return new LoggedInUser_1(this, {
            canSeeHidden: this.canSeeHidden,
            email: this.email,
            emailVerified: this.emailVerified,
            sessions: newSessionsArray,
            loginMethod: this.loginMethod,
            permissions: this.permissions,
            fullyLoaded: this.fullyLoaded,
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateFromApi(data) {
        return new LoggedInUser_1(data, {
            canSeeHidden: this.canSeeHidden,
            email: data.email,
            emailVerified: data.emailVerified,
            sessions: this.sessions,
            loginMethod: this.loginMethod,
            permissions: data.permissions ?? this.permissions,
            fullyLoaded: this.fullyLoaded,
        });
    }
    /**
     * Returns a new user instance with the email address updated.
     *
     * @param params The new email address and its verification status.
     * @returns A new `LoggedInUser` with the updated email fields.
     */
    updateEmail({ email, emailVerified }) {
        return new LoggedInUser_1(this, {
            canSeeHidden: this.canSeeHidden,
            email: Maybe.of(email),
            emailVerified: Maybe.of(emailVerified),
            sessions: this.sessions,
            loginMethod: this.loginMethod,
            permissions: this.permissions,
            fullyLoaded: this.fullyLoaded,
        });
    }
    /**
     * Check whether the user normally would have to pay for the course.
     *
     * This does not yet check if they have paid already!
     *
     * @param tenantId The tenant id if we have any.
     * @returns Whether we should pay for the course or not in the general sense.
     */
    shouldPayForCourses(tenantId) {
        return !UserStore.hasTenantPermission()(GPerm.can_skip_payment, tenantId);
    }
    /**
     * Get whether the user should pay for a course in the general sense.
     *
     * This does not yet check if they have paid already!
     *
     * @param course The course to check has to be paid.
     * @returns Whether the user should pay for the course in the general sense.
     */
    shouldPayFor(course) {
        const price = course.price;
        if (price.isJust() && this.shouldPayForCourses(price.value.course.tenantId)) {
            return price;
        }
        return Maybe.empty();
    }
    /**
     * Check whether the user has the permission to skip payment for a course.
     *
     * @param course The course to check the payment status for.
     * @returns Whether the user has permission to skip the payment.
     */
    canSkipPaymentFor(course) {
        return UserStore.hasTenantPermission()(GPerm.can_skip_payment, course.tenantId);
    }
    /**
     * A getter to retrieve the current state of the student view.
     *
     * If the user has no removed course permissions, the state is 'disabled'.
     * Otherwise, the state is 'enabled' and indicates whether the user can leave the session.
     * We can only leave if we have a base session token to fall back to.
     *
     * @returns An object representing the state of the student view.
     */
    getStudentViewState() {
        const removedPerms = this.session.restrictions.removedPermissions?.course ?? new Set();
        if (removedPerms.size === 0) {
            return Object.freeze({ tag: 'disabled' });
        }
        return Object.freeze({
            tag: 'enabled',
            canLeave: this.sessions.length > 1,
        });
    }
    /** @returns The current exam session state. */
    getExamSessionState() {
        const forContext = this.session.restrictions.forContext;
        if (forContext?.isolated !== true) {
            return Object.freeze({ tag: 'disabled' });
        }
        return Object.freeze({
            tag: 'enabled',
            courseId: forContext.courseId,
        });
    }
    /** @returns This logged in user as APIv2 extended user. */
    toAPIv2() {
        return new apiModels.ExtendedUser({
            email: this.email,
            type: 'normal',
            hidden: false,
            id: this.id,
            isTestStudent: this.isTestStudent,
            name: this.name,
            tenantId: this.tenantId,
            username: this.username,
            permissions: this.permissions,
            emailVerified: this.emailVerified,
        });
    }
};
LoggedInUser = LoggedInUser_1 = __decorate([
    immutable.immutable
], LoggedInUser);
export { LoggedInUser };
/**
 * Create a `NormalUser` model from Apiv2 data.
 *
 * @param normalUser The data received from the api.
 * @returns A `User` model for this user.
 */
function makeNormalUserFromApiV2(normalUser) {
    return new apiModels.NormalUser({
        id: parseInt(normalUser.id, 10),
        username: normalUser.username,
        name: Maybe.fromNullable(normalUser.name),
        tenantId: normalUser.tenantId,
        isTestStudent: normalUser.isTestStudent,
        type: 'normal',
    });
}
/**
 * Create a `User` model from Apiv2 data.
 *
 * @param data The data received from the api.
 * @returns A `User` model for this user.
 */
export function makeUserFromApiv2(data) {
    return expr.visit(data, {
        group: (groupUser) => new apiModels.GroupUser({
            id: parseInt(groupUser.id, 10),
            username: groupUser.username,
            tenantId: groupUser.tenantId,
            isTestStudent: false,
            type: 'group-user',
            group: new apiModels.Group({
                createdAt: moment(groupUser.group.createdAt),
                groupSetId: parseInt(groupUser.group.groupSetId, 10),
                id: parseInt(groupUser.group.id, 10),
                members: groupUser.group.members.map(m => makeNormalUserFromApiV2(m)),
                name: groupUser.group.name,
            }),
        }),
        normal: makeNormalUserFromApiV2,
        _: type => {
            throw new Error(`Unknown user type encountered: ${String(type)}`);
        },
    });
}
